// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCollateralAgreementsV2 } from '@hooks/queries/collateralv2/collateral/useCollateralAgreementsV2';
import { useCallback, useMemo } from 'react';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v2/lib/Utility/Collateral/App/Model/Collateral/index';
import { ContractId } from '@daml/types';
import TemplateContract from '../../../utils/TemplateContract';
import { CollateralKey, CollateralKeyArgs, getCollateralKey } from './collateralKey';

export const useGetCollateralAgreement = () => {
  const collateralAgreements = useCollateralAgreementsV2();

  const maps = useMemo(() => {
    const keyToCollateralAgreement = new Map<
      CollateralKey,
      TemplateContract<CollateralAgreement>
    >();
    const contractIdToCollateralAgreement = new Map<
      ContractId<CollateralAgreement>,
      TemplateContract<CollateralAgreement>
    >();

    collateralAgreements.data?.forEach((agreement) => {
      keyToCollateralAgreement.set(getCollateralKey(agreement.payload), agreement);

      contractIdToCollateralAgreement.set(agreement.contractId, agreement);
    });

    return {
      keyToCollateralAgreement,
      contractIdToCollateralAgreement,
    };
  }, [collateralAgreements]);

  const getCollateralAgreement = useCallback(
    (args: CollateralKeyArgs) => {
      return maps.keyToCollateralAgreement.get(getCollateralKey(args));
    },
    [maps],
  );

  const getCollateralAgreementByContractId = useCallback(
    (contractId: ContractId<CollateralAgreement>) => {
      return maps.contractIdToCollateralAgreement.get(contractId);
    },
    [maps],
  );

  return {
    getCollateralAgreement,
    getCollateralAgreementByContractId,
  };
};
